import { motion } from 'framer-motion';

interface AnimatedLogoProps {
    className?: string;
    variant?: 'processing' | 'loading' | 'default';
    size?: 'sm' | 'md' | 'lg' | 'xl';
}

export const AnimatedLogo = ({ className = '', variant = 'default', size = 'lg' }: AnimatedLogoProps) => {

    // Size mapping
    const sizeClasses = {
        sm: 'w-8 h-8',
        md: 'w-12 h-12',
        lg: 'w-20 h-20',
        xl: 'w-32 h-32'
    };

    const containerSize = sizeClasses[size];

    return (
        <div className={`relative ${containerSize} flex items-center justify-center ${className}`}>
            {/* Pulsing background aura - Only for processing/loading variants */}
            {variant !== 'default' && (
                <motion.div
                    animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.2, 0.5, 0.2],
                    }}
                    transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                    className="absolute inset-0 bg-accent-primary/20 rounded-full blur-2xl"
                />
            )}

            {/* Logo with animated parts */}
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`${containerSize} relative z-10`}
            >
                {/* Top-left rect - Animate scale and opacity */}
                <motion.rect
                    width="7"
                    height="9"
                    x="3"
                    y="3"
                    rx="1"
                    stroke="currentColor"
                    className="text-neutral-700 dark:text-neutral-300"
                    animate={variant !== 'default' ? {
                        scale: [1, 1.08, 1],
                    } : {}}
                    transition={{
                        duration: 1.8,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 0
                    }}
                    style={{ transformOrigin: "6.5px 7.5px" }}
                />

                {/* Top-right rect - Different timing & ACCENT COLOR */}
                <motion.rect
                    width="7"
                    height="5"
                    x="14"
                    y="3"
                    rx="1"
                    stroke="currentColor"
                    className="text-accent-primary"
                    animate={variant !== 'default' ? {
                        scale: [1, 1.08, 1],
                    } : {}}
                    transition={{
                        duration: 1.8,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 0.45
                    }}
                    style={{ transformOrigin: "17.5px 5.5px" }}
                />

                {/* Bottom-right rect */}
                <motion.rect
                    width="7"
                    height="9"
                    x="14"
                    y="12"
                    rx="1"
                    stroke="currentColor"
                    className="text-neutral-700 dark:text-neutral-300"
                    animate={variant !== 'default' ? {
                        scale: [1, 1.08, 1],
                    } : {}}
                    transition={{
                        duration: 1.8,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 0.9
                    }}
                    style={{ transformOrigin: "17.5px 16.5px" }}
                />

                {/* Bottom-left rect */}
                <motion.rect
                    width="7"
                    height="5"
                    x="3"
                    y="16"
                    rx="1"
                    stroke="currentColor"
                    className="text-neutral-700 dark:text-neutral-300"
                    animate={variant !== 'default' ? {
                        scale: [1, 1.08, 1],
                    } : {}}
                    transition={{
                        duration: 1.8,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1.35
                    }}
                    style={{ transformOrigin: "6.5px 18.5px" }}
                />
            </svg>
        </div>
    );
};
